/*
 * Persistence - Settings, Content und Editor-State Speicherung
 *
 * Verwaltet das Laden und Speichern von:
 * - Anwendungs-Einstellungen (Display Mode, Font, Layout) → localStorage
 * - Editor-Inhalt (Paragraphen mit RT-Spans) → IndexedDB via ProjectManager
 * - Editor-State (Cursorposition, Titel) → IndexedDB via ProjectManager
 *
 * Settings werden gebündelt einmal pro Frame gespeichert (_flushSettings).
 * Content wird mit 1s Debounce über den ProjectManager in IndexedDB geschrieben.
 */

import { ctx } from './appContext.js'
import { FONT_SIZE, DISPLAY_MODES, PaneId } from './config.js'

// === LocalStorage Keys ===
const STORAGE_KEY = 'carbisdale_settings'

// Legacy-Keys (nur für Migration gelesen, nie mehr geschrieben)
const LEGACY_CONTENT_KEY = 'carbisdale_content'
const LEGACY_EDITOR_STATE_KEY = 'carbisdale_editor_state'

/**
 * Lädt gespeicherte Einstellungen aus localStorage.
 *
 * @returns {Object|null} Gespeicherte Settings oder null
 */
export function loadSettings() {
    try {
        const stored = localStorage.getItem(STORAGE_KEY)
        if (stored) {
            return JSON.parse(stored)
        }
    } catch (error) {
        console.warn('Failed to load settings:', error)
    }
    return null
}

/**
 * Markiert Settings als geändert — tatsächliche Speicherung erfolgt
 * gebündelt im nächsten Render-Frame (_flushSettings).
 */
export function saveSettings() {
    ctx._settingsDirty = true
}

/**
 * Schreibt Settings in localStorage, falls seit dem letzten Flush etwas geändert wurde.
 * Wird einmal pro Render-Frame aus der Main-Loop aufgerufen.
 */
export function _flushSettings() {
    if (!ctx._settingsDirty) return
    ctx._settingsDirty = false
    try {
        const settings = {
            displayMode: ctx.renderer.getDisplayMode(),
            fontSize: ctx.renderer.getFontSize(),
            layout: ctx.layout ? ctx.layout.getState() : null,
            // Spellcheck-Sprache: null = Off, 'de'/'en' = Sprache.
            // Cloud-Persistenz kommt mit Phase 2 Server-Schritt; bis dahin
            // ueberlebt der Toggle im localStorage.
            spellcheckLang: ctx.spellCheckManager
                ? ctx.spellCheckManager.getLanguage()
                : (ctx._pendingSpellcheckLang !== undefined ? ctx._pendingSpellcheckLang : 'de')
        }
        localStorage.setItem(STORAGE_KEY, JSON.stringify(settings))
    } catch (error) {
        console.warn('Failed to save settings:', error)
    }
}

/**
 * Wendet gespeicherte Einstellungen auf den Renderer an.
 *
 * @param {Object} settings - Die zu ladenden Settings
 */
export async function applySettings(settings) {
    if (!settings) return

    // Migration: alte Display-Mode-Namen auf neue mappen
    let mode = settings.displayMode
    if (mode && !DISPLAY_MODES[mode]) {
        const oldFont = settings.font
        const migration = {
            'NERD': oldFont === 'DINA' ? 'ULTRA_NERD' : 'REGULAR_NERD',
            'DARK': oldFont === 'GARAMOND' ? 'ARTIST_DARK' : 'WORKER_DARK',
            'LIGHT': oldFont === 'GARAMOND' ? 'ARTIST' : 'WORKER',
            'DARK_COLORED': oldFont === 'NOTO' ? 'MODERN_NERD' : 'WORKER_DARK',
            'LIGHT_COLORED': 'WORKER'
        }
        mode = migration[mode] || 'ULTRA_NERD'
    }

    // Display Mode anwenden (impliziert den Font)
    if (mode && DISPLAY_MODES[mode]) {
        ctx.renderer.setDisplayMode(mode)
        const modeConfig = DISPLAY_MODES[mode]
        if (modeConfig.font) {
            ctx.renderer.currentFont = modeConfig.font
        }
    }

    // Font-Größe anwenden
    if (settings.fontSize) {
        ctx.renderer.fontSize = Math.max(FONT_SIZE.min, Math.min(FONT_SIZE.max, settings.fontSize))
    }

    // Spellcheck-Sprache merken — der SpellCheckManager existiert hier noch
    // nicht (applySettings laeuft VOR dem Bootstrap), also legen wir den
    // Wert ab und das Spellcheck-Bootstrap nutzt ihn.
    if (Object.prototype.hasOwnProperty.call(settings, 'spellcheckLang')) {
        ctx._pendingSpellcheckLang = settings.spellcheckLang
    }

    // Layout-State wird später angewendet, wenn layout erstellt ist
}

/**
 * Wendet gespeicherte Layout-Einstellungen an.
 *
 * @param {Object} settings - Die zu ladenden Settings
 */
export function applyLayoutSettings(settings) {
    if (!settings || !settings.layout || !ctx.layout) return
    ctx.layout.setState(settings.layout)
    // Cursor auf aktiven Bereich setzen
    ctx.layout.exitNavigationMode()
}

/**
 * Speichert den Editor-State (Cursorposition und Dokumenten-Titel) im Projekt.
 */
export function saveEditorState() {
    if (!ctx.editor || !ctx.projectManager) return

    try {
        const cursorPos = ctx.editor.getCursorPosition()
        if (cursorPos) {
            const paragraphIndex = ctx.editor.model.getParagraphIndex(cursorPos.paragraphId)
            const cursorLineIndex = ctx.editor.view.getCursorLineIndex(
                cursorPos.paragraphId, cursorPos.offset, cursorPos.field || 'text')
            const cursorScreenY = cursorLineIndex >= 0 ? cursorLineIndex - ctx.editor.view.viewportStart : 0

            const cursorState = {
                paragraphId: cursorPos.paragraphId,
                paragraphIndex,
                offset: cursorPos.offset,
                cursorScreenY,
                activeField: cursorPos.field || 'text'
            }

            // Fire-and-forget — kein await nötig im Render-Loop
            ctx.projectManager.saveProjectState(cursorState)
        }
    } catch (error) {
        console.warn('Failed to save editor state:', error)
    }
}

/**
 * Markiert geaenderte Paragraphen als dirty und plant einen IndexedDB-Flush.
 * Nutzt das Dirty-Tracking des Editor-Models fuer praezise Erkennung.
 */
export function saveContent() {
    if (!ctx.editor || !ctx.projectManager) return

    const model = ctx.editor.model
    // Waehrend eines Remote-Apply-Blocks nichts persistieren — der Inhalt
    // stammt gerade frisch vom Server und wuerde sonst als lokale
    // Aenderung zurueckgesynct.
    if (model.isApplyingRemote()) return

    const dirtyLevel = model.getDirtyLevel()

    if (dirtyLevel === 'PARAGRAPH' && model._dirtyParagraphIds.size > 0) {
        // Nur die tatsaechlich geaenderten Paragraphen markieren
        for (const id of model._dirtyParagraphIds) {
            ctx.projectManager.markDirty(id)
        }
    } else if (dirtyLevel === 'STRUCTURAL') {
        if (model._dirtyParagraphIds.size > 0) {
            // Inkrementell: nur tatsaechlich geaenderte Absaetze markieren
            for (const id of model._dirtyParagraphIds) {
                ctx.projectManager.markDirty(id)
            }
        } else if (model._allContentDirty) {
            // Undo/Import: wir wissen nicht welche Absaetze sich geaendert haben → alle markieren
            const ids = model.paragraphs.map(p => p.id)
            ctx.projectManager.markAllDirty(ids)
        }
        // markStructuralChange-Entkopplung (Session B Followup 2026-05-24,
        // Lesson 96 erweitert):
        //
        // EditorModel.replaceParagraphText, setParagraphBlock und ein gutes
        // Dutzend andere Single-Paragraph-Mutationen setzen `_dirtyLevel =
        // STRUCTURAL` per `_markStructuralDirtyIncremental` — gerechtfertigt
        // fuer den RENDERER (Block-Felder koennen Layout-Hoehe der
        // Nachbarabsaetze aendern, Full-Rebuild noetig), aber NICHT fuer die
        // Persistence: `markStructuralChange()` setzt `_structuralDirty=true`
        // im ProjectManager, was den Hash-Skip in flushNow (Z. 689)
        // deaktiviert und dadurch ALLE Paragraphen rehasht. Mit
        // `includeProjectState:true`-Serialisierung schleichen sich Bumps
        // auf nicht-betroffene Paragraphen ein (activeCollapsed/
        // sectionCollapsed im Hash) — Versions-Inflation, langfristig
        // version_mismatch-Risiko in Multi-Device-Szenarien.
        //
        // Fix: markStructuralChange nur bei *echter* Struktur-Aenderung —
        // entweder unbekannter Diff (_allContentDirty: Undo/Import) ODER
        // paragraphOrder hat sich tatsaechlich gegenueber dem persistierten
        // Stand veraendert (Insert/Delete/Move/Reorder). Reine
        // Single-Paragraph-Block-Aenderungen kommen mit `markDirty(id)` aus,
        // der Renderer bleibt unberuehrt (dirtyLevel === STRUCTURAL fuer
        // den nutzt nach wie vor seinen Full-Rebuild-Pfad).
        const proj = ctx.projectManager.getCurrentProject()
        const needsStructural = model._allContentDirty
            || (proj && _paragraphOrderChanged(proj.paragraphOrder, model.paragraphs))
        if (needsStructural) {
            ctx.projectManager.markStructuralChange()
        }
        // Section Lock fuer ersten Abschnitt (ohne Separator) am Projekt speichern
        if (proj) {
            proj.firstSectionLocked = !!model._firstSectionLocked
        }
    } else {
        // CURSOR_ONLY oder CLEAN — nichts zu speichern
        return
    }

    ctx.projectManager.scheduleFlush()
}

/**
 * Vergleicht die persistierte paragraphOrder mit der aktuellen
 * Editor-Reihenfolge. Liefert true, sobald sich Laenge oder eine ID an
 * derselben Position unterscheidet. Pure Function, O(n) bei Linearzeit-
 * Vergleich von String-Pointern — vernachlaessigbar gegenueber dem
 * Hash-Rehash, den wir damit einsparen.
 *
 * Liefert true auch wenn `oldOrder` null/undefined ist (frischer Project-
 * Slot ohne Order) — defensiver: lieber einmal sicherheitshalber strukturell
 * bumpen, als eine echte Order-Einrichtung verpassen.
 *
 * @param {string[]|null|undefined} oldOrder
 * @param {Array<{id:string}>} newParagraphs
 * @returns {boolean}
 */
function _paragraphOrderChanged(oldOrder, newParagraphs) {
    if (!Array.isArray(oldOrder)) return true
    if (oldOrder.length !== newParagraphs.length) return true
    for (let i = 0; i < oldOrder.length; i++) {
        if (oldOrder[i] !== newParagraphs[i].id) return true
    }
    return false
}

/**
 * Speichert den Editor-Inhalt sofort in IndexedDB (für beforeunload).
 * Nutzt die gleiche Dirty-Erkennung wie saveContent() — nur tatsächlich
 * geänderte Absätze werden geflusht, um Version-Inflation zu vermeiden.
 */
export function saveContentImmediately() {
    if (!ctx.editor || !ctx.projectManager) return

    const model = ctx.editor.model
    // Gleicher Guard wie in saveContent(): waehrend Remote-Apply nichts
    // persistieren/flushen.
    if (model.isApplyingRemote()) {
        saveEditorState()
        return
    }

    const dirtyLevel = model.getDirtyLevel()

    if (dirtyLevel === 'PARAGRAPH' && model._dirtyParagraphIds.size > 0) {
        for (const id of model._dirtyParagraphIds) {
            ctx.projectManager.markDirty(id)
        }
    } else if (dirtyLevel === 'STRUCTURAL') {
        if (model._dirtyParagraphIds.size > 0) {
            for (const id of model._dirtyParagraphIds) {
                ctx.projectManager.markDirty(id)
            }
        } else if (model._allContentDirty) {
            const ids = model.paragraphs.map(p => p.id)
            ctx.projectManager.markAllDirty(ids)
        }
        // Immer markStructuralChange — auch bei reinen Loeschungen ohne Content-Dirty
        ctx.projectManager.markStructuralChange()
    } else {
        // CLEAN oder CURSOR_ONLY — nur Cursor-State sichern
        saveEditorState()
        return
    }

    // Sofort flushen (fire-and-forget, Browser schliesst IDB-Transactions i.d.R. ab)
    ctx.projectManager.flush(() => ctx.editor.model.paragraphs)

    // Editor-State auch speichern
    saveEditorState()
}

/**
 * Bestimmt beim Restore das effektive Cursor-Feld und das Ziel-Pane fuer
 * einen Separator-Input. Regeln:
 *   - Separator → immer 'text' (Separator-Label lebt im text-Feld). Pane aus Filter-Mode.
 *   - Main unsichtbar (Filter aktiv) → Feld aus Pane ('info'/'note'), sonst wuerde
 *     der Cursor im unsichtbaren text-Feld eines Nicht-Separator-Absatzes landen.
 *   - Main sichtbar → gespeichertes Feld, falls dessen Pane sichtbar ist; sonst 'text'.
 */
function _resolveRestoreField(para, savedField) {
    const layout = ctx.layout
    const mainVisible = !!(layout && layout.panes[PaneId.MAIN]?.isVisible())
    const outlineVisible = !!(layout && layout.panes[PaneId.OUTLINE]?.isVisible())
    const contextVisible = !!(layout && layout.panes[PaneId.CONTEXT]?.isVisible())

    let separatorPane = 'main'
    if (!mainVisible && contextVisible && !outlineVisible) separatorPane = 'context'
    else if (!mainVisible && outlineVisible && !contextVisible) separatorPane = 'outline'
    else if (!mainVisible && contextVisible) separatorPane = 'context'
    else if (!mainVisible && outlineVisible) separatorPane = 'outline'

    if (para.type === 'separator') {
        return { field: 'text', separatorPane }
    }

    if (!mainVisible) {
        if (contextVisible && !outlineVisible) return { field: 'info', separatorPane }
        if (outlineVisible && !contextVisible) return { field: 'note', separatorPane }
        // 'both' sichtbar → gespeichertes Feld beibehalten, sonst Fallback auf info
        if (savedField === 'note' || savedField === 'info') return { field: savedField, separatorPane }
        return { field: 'info', separatorPane }
    }

    // Main sichtbar: gespeichertes Feld respektieren, wenn zugehoeriges Pane sichtbar
    if (savedField === 'note' && outlineVisible) return { field: 'note', separatorPane }
    if (savedField === 'info' && contextVisible) return { field: 'info', separatorPane }
    return { field: 'text', separatorPane }
}

/**
 * Stellt den Cursor aus dem gespeicherten Projekt-State wieder her.
 * @param {Object} cursorState - Der gespeicherte Cursor-State
 */
export function restoreCursorFromProjectState(cursorState) {
    if (!ctx.editor || !cursorState) return

    // Absatz per paragraphId oder paragraphIndex finden
    let para = null
    if (cursorState.paragraphId) {
        para = ctx.editor.model.paragraphs.find(p => p.id === cursorState.paragraphId)
    }
    if (!para && cursorState.paragraphIndex !== undefined) {
        para = ctx.editor.model.paragraphs[cursorState.paragraphIndex]
    }
    if (!para) return

    // Effektives Feld aus Pane-Zustand bestimmen (ueberschreibt ggf. das gespeicherte
    // Feld, damit der Cursor im aktuell sichtbaren Pane landet — sonst in Context-Max
    // nach Reload unsichtbar).
    const { field: effectiveField, separatorPane } = _resolveRestoreField(
        para, cursorState.activeField || 'text')

    // Offset auf die richtige Feld-Laenge klammern (setCursor macht das auch,
    // aber wir berechnen hier bewusst gegen das Ziel-Feld).
    const fieldText = ctx.editor.model._getFieldText(para, effectiveField)
    const offset = Math.min(cursorState.offset || 0, fieldText.length)
    ctx.editor.model.setCursor(para.id, offset, effectiveField)

    // Separator: Separator-Input im passenden Pane aktivieren. Ohne diesen Aufruf
    // haengt sepState.pane auf 'main' und der Cursor wird bei Main-Hidden-Layouts
    // von keinem Pane gerendert.
    if (para.type === 'separator') {
        ctx.editor.input._activateSeparatorInput(offset, separatorPane)
    }

    // Viewport wiederherstellen
    if (cursorState.cursorScreenY !== undefined) {
        ctx.editor.view.rebuildIfDirty()
        // Feld-bewusst: Note-Cursor (Outline) liegt nicht in lines[], sondern in
        // noteLines[] (Co-Location) — sonst -1 und der Viewport bleibt ungescrollt.
        const cursorLineIndex = ctx.editor.view.getCursorLineIndex(para.id, offset, effectiveField)
        if (cursorLineIndex >= 0) {
            ctx.editor.view.scrollTo(cursorLineIndex - cursorState.cursorScreenY)
        }
        ctx.editor.view.setPendingCursorScreenY(cursorState.cursorScreenY)
    } else {
        ctx.editor.view.ensureCursorVisible()
    }
}
